/**
 * ClinicianPortalAdmin — the minimal staff surface for the clinician referral
 * portal (clinician-portal P0 §6; the fuller management UI is P2).
 *
 * THREE TABS, because these are three different jobs done by different people at
 * different moments, and stacking them on one page would make each harder to do:
 *   · Practices & users — the P0 surface, unchanged.
 *   · Referrals — the staff list, collision triage (the only staff decrypt path in
 *     P1) and manual entry for a referral that arrived by fax or phone (§6.11b).
 *   · Grants — who can see which patient. P0 shipped the revoke primitive and no
 *     way to see what there was to revoke, which made it unreachable (§2.8 F-7).
 *
 * THE VERIFICATION CEREMONY IS THE POINT. Sending an invite is the LAST step of a
 * human process (NPI registry check, then a call-back to a practice number found in
 * an independent directory — never a number supplied in the request). The invite
 * email is a security control, not a mailing, so this screen states that inline
 * rather than assuming everyone remembers.
 *
 * Disabling a user revokes their live sessions in the same request, so a disabled
 * clinician is signed out of the portal immediately rather than at next expiry.
 *
 * Review §C14: the tab strip now opens where it is sent. Today's collision card
 * and the Settings nav badge deep-link here with `?tab=referrals&status=…`, and
 * dropping someone on Practices & users after they clicked "Review them" is how
 * a queue stays unworked. The Referrals count is fetched by this page rather
 * than reported up from the panel, because the panel only mounts on its own tab
 * — a badge that appears once you are already looking at the thing it counts is
 * the §C14 defect restated one level up.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Building2, KeyRound, Loader2, MonitorSmartphone, Pencil, Plus, ScrollText, ShieldCheck, Send, UserPlus } from 'lucide-react';
import api from '../../../services/api';
import PageHeader, { RefreshButton } from '../../components/PageHeader';
import Modal from '../../components/forms/Modal';
import { FilterBar, FilterSelect, SearchInput } from '../../components/filters';
import { dangerBtnCls, fieldCls, iconBtnCls, primaryBtnCls, secondaryBtnCls } from '../../components/filters/styles';
import PortalReferralsPanel from './PortalReferralsPanel';
import PortalGrantsPanel from './PortalGrantsPanel';
import PortalAuditPanel from './PortalAuditPanel';
import { listReferrals, summariseCollisions } from '../../portalReferrals';
import { Alert, Badge, type BadgeTone } from '../../components/ui';

const CREDENTIALS = ['MD', 'DO', 'NP', 'PA', 'RN', 'staff'];
const ROLES = [
  { value: 'provider', label: 'Provider (refers patients)' },
  { value: 'staff', label: 'Practice staff (submits on their behalf)' },
  { value: 'org_admin', label: 'Practice admin (manages their own users)' },
];

const STATUS_TONE: Record<string, BadgeTone> = {
  active: 'green',
  invited: 'amber',
  disabled: 'gray',
};

const errMsg = (e: any): string =>
  e?.response?.data?.detail || e?.response?.data?.error || e?.message || 'Request failed';

interface PortalOrg {
  id: string;
  name: string;
  npi_org: string | null;
  phone: string | null;
  fax: string | null;
  /** Optional on purpose — older list payloads may not carry it; the edit modal treats missing as blank. */
  address?: string | null;
  status: 'active' | 'disabled';
  users: { total: number; active: number; invited: number; disabled: number };
}

/** GET /api/crm/portal-admin/users/:id/sessions row. */
interface PortalSession {
  id: string;
  created_at: string;
  last_seen_at: string | null;
  expires_at: string | null;
  revoked_at: string | null;
  revoke_reason: string | null;
  ip: string | null;
  user_agent: string | null;
  live: boolean;
}

interface PortalUser {
  id: string;
  org_id: string;
  org_name: string | null;
  org_status: string | null;
  email: string;
  name: string;
  credential: string | null;
  role: string;
  status: 'invited' | 'active' | 'disabled';
  mobile_phone: string | null;
  last_login_at: string | null;
  invite_pending: boolean;
  invite_expired: boolean;
}

// ---------------------------------------------------------------------------
function CreateOrgModal({ onClose, onSaved }: { onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState('');
  const [npi, setNpi] = useState('');
  const [phone, setPhone] = useState('');
  const [fax, setFax] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    if (!name.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.post('/api/crm/portal-admin/orgs', {
        name: name.trim(),
        npi_org: npi.trim() || undefined,
        phone: phone.trim() || undefined,
        fax: fax.trim() || undefined,
      });
      onSaved();
      onClose();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen onClose={busy ? () => {} : onClose} title="Add a referring practice">
      <div className="space-y-3">
        <div>
          <label htmlFor="portal-org-name" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Practice name
          </label>
          <input
            id="portal-org-name"
            className={fieldCls}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Northside Family Medicine"
            autoFocus
          />
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="portal-org-phone" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Phone
            </label>
            <input id="portal-org-phone" className={fieldCls} value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>
          <div>
            <label htmlFor="portal-org-fax" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Fax
            </label>
            <input id="portal-org-fax" className={fieldCls} value={fax} onChange={(e) => setFax(e.target.value)} />
          </div>
        </div>
        <div>
          <label htmlFor="portal-org-npi" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Organisation NPI <span className="font-normal text-gray-500">(optional)</span>
          </label>
          <input id="portal-org-npi" className={fieldCls} value={npi} onChange={(e) => setNpi(e.target.value)} />
        </div>
        {error && (
          <Alert tone="error" icon={false}>{error}</Alert>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" className={secondaryBtnCls} onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className={primaryBtnCls} onClick={save} disabled={busy || !name.trim()}>
            {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            Add practice
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
function EditOrgModal({ org, onClose, onSaved }: { org: PortalOrg; onClose: () => void; onSaved: () => void }) {
  const [name, setName] = useState(org.name);
  const [npi, setNpi] = useState(org.npi_org || '');
  const [phone, setPhone] = useState(org.phone || '');
  const [fax, setFax] = useState(org.fax || '');
  const [address, setAddress] = useState(org.address || '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    if (!name.trim() || busy) return;
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/api/crm/portal-admin/orgs/${org.id}`, {
        name: name.trim(),
        npi_org: npi.trim() || null,
        phone: phone.trim() || null,
        fax: fax.trim() || null,
        address: address.trim() || null,
      });
      onSaved();
      onClose();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen onClose={busy ? () => {} : onClose} title="Edit practice">
      <div className="space-y-3">
        <div>
          <label htmlFor="portal-org-edit-name" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Practice name
          </label>
          <input
            id="portal-org-edit-name"
            className={fieldCls}
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoFocus
          />
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="portal-org-edit-phone" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Phone
            </label>
            <input id="portal-org-edit-phone" className={fieldCls} value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>
          <div>
            <label htmlFor="portal-org-edit-fax" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Fax
            </label>
            <input id="portal-org-edit-fax" className={fieldCls} value={fax} onChange={(e) => setFax(e.target.value)} />
          </div>
        </div>
        <div>
          <label htmlFor="portal-org-edit-npi" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Organisation NPI <span className="font-normal text-gray-500">(optional)</span>
          </label>
          <input id="portal-org-edit-npi" className={fieldCls} value={npi} onChange={(e) => setNpi(e.target.value)} />
        </div>
        <div>
          <label htmlFor="portal-org-edit-address" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Address <span className="font-normal text-gray-500">(optional)</span>
          </label>
          <input id="portal-org-edit-address" className={fieldCls} value={address} onChange={(e) => setAddress(e.target.value)} />
        </div>
        {error && (
          <Alert tone="error" icon={false}>{error}</Alert>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" className={secondaryBtnCls} onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className={primaryBtnCls} onClick={save} disabled={busy || !name.trim()}>
            {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            Save practice
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
function InviteUserModal({
  orgs,
  onClose,
  onSaved,
}: {
  orgs: PortalOrg[];
  onClose: () => void;
  onSaved: (note: string | null) => void;
}) {
  const activeOrgs = orgs.filter((o) => o.status === 'active');
  const [orgId, setOrgId] = useState(activeOrgs[0]?.id || '');
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [credential, setCredential] = useState('MD');
  const [role, setRole] = useState('provider');
  const [mobile, setMobile] = useState('');
  const [verified, setVerified] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const canSend = orgId && name.trim() && email.trim().includes('@') && verified && !busy;

  const send = async () => {
    if (!canSend) return;
    setBusy(true);
    setError(null);
    try {
      const res = await api.post('/api/crm/portal-admin/users', {
        org_id: orgId,
        name: name.trim(),
        email: email.trim(),
        credential,
        role,
        mobile_phone: mobile.trim() || undefined,
      });
      onSaved(res.data?.email_error || null);
      onClose();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen onClose={busy ? () => {} : onClose} title="Invite a clinician">
      <div className="space-y-3">
        <div>
          <label htmlFor="portal-user-org" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Practice
          </label>
          <select id="portal-user-org" className={fieldCls} value={orgId} onChange={(e) => setOrgId(e.target.value)}>
            {activeOrgs.length === 0 && <option value="">Add a practice first</option>}
            {activeOrgs.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="portal-user-name" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Full name
            </label>
            <input id="portal-user-name" className={fieldCls} value={name} onChange={(e) => setName(e.target.value)} />
          </div>
          <div>
            <label htmlFor="portal-user-cred" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Credential
            </label>
            <select id="portal-user-cred" className={fieldCls} value={credential} onChange={(e) => setCredential(e.target.value)}>
              {CREDENTIALS.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div>
          <label htmlFor="portal-user-email" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Work email
          </label>
          <input
            id="portal-user-email"
            type="email"
            className={fieldCls}
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            aria-describedby="portal-user-email-help"
          />
          <p id="portal-user-email-help" className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            The invite link is bound to this address — they cannot sign in with any other.
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="portal-user-role" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Role
            </label>
            <select id="portal-user-role" className={fieldCls} value={role} onChange={(e) => setRole(e.target.value)}>
              {ROLES.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="portal-user-mobile" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Mobile <span className="font-normal text-gray-500">(optional)</span>
            </label>
            <input
              id="portal-user-mobile"
              className={fieldCls}
              value={mobile}
              onChange={(e) => setMobile(e.target.value)}
              aria-describedby="portal-user-mobile-help"
            />
            <p id="portal-user-mobile-help" className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Lets them get sign-in codes by text instead of email.
            </p>
          </div>
        </div>

        {/* The ceremony gate. Not decoration: an invite sent without it is the one
            step that could give a stranger a portal account. */}
        <label className="flex items-start gap-2 rounded-lg bg-amber-50 px-3 py-2.5 text-sm text-amber-900 dark:bg-amber-900/20 dark:text-amber-200">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 rounded border-gray-300"
            checked={verified}
            onChange={(e) => setVerified(e.target.checked)}
          />
          <span>
            I have verified this clinician: checked the NPI registry <strong>and</strong> called the practice back on a
            number from an independent directory — not one supplied in the request.
          </span>
        </label>

        {error && (
          <Alert tone="error" icon={false}>{error}</Alert>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" className={secondaryBtnCls} onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className={primaryBtnCls} onClick={send} disabled={!canSend}>
            {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            Send invite
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
function EditUserModal({
  user,
  orgs,
  onClose,
  onSaved,
}: {
  user: PortalUser;
  orgs: PortalOrg[];
  onClose: () => void;
  onSaved: (note: string) => void;
}) {
  const activeOrgs = orgs.filter((o) => o.status === 'active');
  // Move targets are ACTIVE practices only (a disabled target answers 409), but
  // the user's current practice still has to render as the initial selection
  // even when it is disabled — so it appears once, marked, at the top.
  const currentOrg = orgs.find((o) => o.id === user.org_id);
  const currentOrgInactive = !!currentOrg && currentOrg.status !== 'active';
  const [name, setName] = useState(user.name);
  const [credential, setCredential] = useState(user.credential || '');
  const [role, setRole] = useState(user.role);
  const [mobile, setMobile] = useState(user.mobile_phone || '');
  const [orgId, setOrgId] = useState(user.org_id);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const orgChanged = orgId !== user.org_id;
  const canSave = !!(name.trim() && orgId && !busy);

  const save = async () => {
    if (!canSave) return;
    setBusy(true);
    setError(null);
    try {
      await api.patch(`/api/crm/portal-admin/users/${user.id}`, {
        name: name.trim(),
        credential: credential || null,
        role,
        mobile_phone: mobile.trim() || null,
        org_id: orgId,
      });
      onSaved(`${name.trim()} updated.`);
      onClose();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen onClose={busy ? () => {} : onClose} title="Edit clinician">
      <div className="space-y-3">
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="portal-user-edit-name" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Full name
            </label>
            <input id="portal-user-edit-name" className={fieldCls} value={name} onChange={(e) => setName(e.target.value)} autoFocus />
          </div>
          <div>
            <label htmlFor="portal-user-edit-cred" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Credential
            </label>
            <select id="portal-user-edit-cred" className={fieldCls} value={credential} onChange={(e) => setCredential(e.target.value)}>
              <option value="">—</option>
              {CREDENTIALS.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
          </div>
        </div>
        <div>
          {/* No email edits here: the sign-in address is identity, not profile. */}
          <span className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">Work email</span>
          <p className="break-all rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5 text-sm text-gray-600 dark:border-gray-700 dark:bg-gray-900/40 dark:text-gray-300">
            {user.email}
          </p>
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Sign-in address — changed by the clinician from their account page, or by revoking this invite and sending a
            new one.
          </p>
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div>
            <label htmlFor="portal-user-edit-role" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Role
            </label>
            <select id="portal-user-edit-role" className={fieldCls} value={role} onChange={(e) => setRole(e.target.value)}>
              {ROLES.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label htmlFor="portal-user-edit-mobile" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
              Mobile <span className="font-normal text-gray-500">(optional)</span>
            </label>
            <input id="portal-user-edit-mobile" className={fieldCls} value={mobile} onChange={(e) => setMobile(e.target.value)} />
          </div>
        </div>
        <div>
          <label htmlFor="portal-user-edit-org" className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
            Practice
          </label>
          <select id="portal-user-edit-org" className={fieldCls} value={orgId} onChange={(e) => setOrgId(e.target.value)}>
            {currentOrgInactive && currentOrg && (
              <option value={currentOrg.id}>{currentOrg.name} (disabled practice)</option>
            )}
            {activeOrgs.map((o) => (
              <option key={o.id} value={o.id}>
                {o.name}
              </option>
            ))}
          </select>
        </div>
        {orgChanged && (
          <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-900 dark:bg-amber-900/20 dark:text-amber-200">
            Moving a clinician changes which practice&apos;s patients they can be granted — existing grants are not
            moved.
          </p>
        )}
        {error && (
          <Alert tone="error" icon={false}>{error}</Alert>
        )}
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" className={secondaryBtnCls} onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button type="button" className={primaryBtnCls} onClick={save} disabled={!canSave}>
            {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            Save changes
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
const SESSION_STATE_PILL: Record<string, string> = {
  Live: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  'Signed out': 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
  Expired: 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
};

function SessionsModal({ user, onClose }: { user: PortalUser; onClose: () => void }) {
  const [sessions, setSessions] = useState<PortalSession[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await api.get(`/api/crm/portal-admin/users/${user.id}/sessions`);
      setSessions(res.data?.sessions || []);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
  }, [user.id]);

  useEffect(() => {
    load();
  }, [load]);

  const signOutEverywhere = async () => {
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const res = await api.post(`/api/crm/portal-admin/users/${user.id}/logout`, {});
      const n = res.data?.sessions_revoked ?? 0;
      setNotice(`${n} session${n === 1 ? '' : 's'} signed out`);
      await load();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const fmtTs = (iso: string | null) =>
    iso ? new Date(iso).toLocaleString(undefined, { dateStyle: 'medium', timeStyle: 'short' }) : '—';
  const stateOf = (s: PortalSession) => (s.live ? 'Live' : s.revoked_at ? 'Signed out' : 'Expired');
  const hasLive = sessions.some((s) => s.live);

  return (
    <Modal isOpen onClose={onClose} title={`Sessions — ${user.name}`} maxWidth="max-w-3xl">
      <div className="space-y-3">
        {error && (
          <Alert tone="error" icon={false}>{error}</Alert>
        )}
        {notice && (
          <Alert tone="success" icon={false}>{notice}</Alert>
        )}
        <div className="overflow-hidden rounded-xl border border-gray-200 dark:border-gray-700">
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
              <thead className="bg-gray-50 dark:bg-gray-800/50">
                <tr>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Started</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Last seen</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Expires</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">IP</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Agent</th>
                  <th className="px-3 py-2 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">State</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
                {loading && (
                  <tr>
                    <td colSpan={6} className="px-3 py-6 text-center">
                      <Loader2 className="mx-auto h-4 w-4 animate-spin text-primary-600 dark:text-primary-400" />
                    </td>
                  </tr>
                )}
                {!loading && sessions.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-3 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                      No sessions — this clinician has never signed in.
                    </td>
                  </tr>
                )}
                {!loading &&
                  sessions.map((s) => {
                    const state = stateOf(s);
                    return (
                      <tr key={s.id} className={state === 'Live' ? '' : 'opacity-70'}>
                        <td className="whitespace-nowrap px-3 py-2 text-xs text-gray-700 dark:text-gray-300">{fmtTs(s.created_at)}</td>
                        <td className="whitespace-nowrap px-3 py-2 text-xs text-gray-700 dark:text-gray-300">{fmtTs(s.last_seen_at)}</td>
                        <td className="whitespace-nowrap px-3 py-2 text-xs text-gray-700 dark:text-gray-300">{fmtTs(s.expires_at)}</td>
                        <td className="whitespace-nowrap px-3 py-2 font-mono text-[11px] text-gray-600 dark:text-gray-400">{s.ip || '—'}</td>
                        <td className="px-3 py-2 text-xs text-gray-600 dark:text-gray-400">
                          {s.user_agent ? (
                            <span className="block max-w-[14rem] truncate" title={s.user_agent}>
                              {s.user_agent}
                            </span>
                          ) : (
                            '—'
                          )}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2">
                          <span
                            className={`inline-flex rounded-full px-2 py-0.5 text-xs font-medium ${SESSION_STATE_PILL[state]}`}
                            title={state === 'Signed out' && s.revoke_reason ? s.revoke_reason : undefined}
                          >
                            {state}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </table>
          </div>
        </div>
        <div className="flex justify-end gap-2 pt-1">
          <button type="button" className={secondaryBtnCls} onClick={onClose}>
            Close
          </button>
          <button type="button" className={dangerBtnCls} onClick={signOutEverywhere} disabled={busy || loading || !hasLive}>
            {busy && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            Sign out everywhere
          </button>
        </div>
      </div>
    </Modal>
  );
}

// ---------------------------------------------------------------------------
const TABS = ['users', 'referrals', 'grants', 'audit'] as const;
type Tab = (typeof TABS)[number];

export default function ClinicianPortalAdmin() {
  const [searchParams] = useSearchParams();
  const [orgs, setOrgs] = useState<PortalOrg[]>([]);
  const [users, setUsers] = useState<PortalUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [notConfigured, setNotConfigured] = useState(false);
  const [orgFilter, setOrgFilter] = useState('');
  const [search, setSearch] = useState('');
  const [showOrg, setShowOrg] = useState(false);
  const [showInvite, setShowInvite] = useState(false);
  const [editOrg, setEditOrg] = useState<PortalOrg | null>(null);
  const [editUser, setEditUser] = useState<PortalUser | null>(null);
  const [sessionsUser, setSessionsUser] = useState<PortalUser | null>(null);
  const [revokeInviteUser, setRevokeInviteUser] = useState<PortalUser | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  // The deep link wins on first render only — after that the tab strip is the
  // user's, so a re-render from an unrelated query change cannot yank them back.
  const [tab, setTab] = useState<Tab>(() => {
    const requested = searchParams.get('tab');
    return (TABS as readonly string[]).includes(requested || '') ? (requested as Tab) : 'users';
  });
  const initialReferralStatus = searchParams.get('status') || '';
  const [collisions, setCollisions] = useState(0);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [o, u] = await Promise.all([
        api.get('/api/crm/portal-admin/orgs'),
        api.get('/api/crm/portal-admin/users'),
      ]);
      setOrgs(o.data?.orgs || []);
      setUsers(u.data?.users || []);
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setLoading(false);
    }
    // Separate from the try above on purpose: a referrals hiccup must not blank
    // the practices and clinicians tables, which are this page's main job.
    try {
      setCollisions(summariseCollisions(await listReferrals('collision_review')).total);
    } catch {
      setCollisions(0);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return users.filter((u) => {
      if (orgFilter && u.org_id !== orgFilter) return false;
      if (!q) return true;
      return (
        u.name.toLowerCase().includes(q) ||
        u.email.toLowerCase().includes(q) ||
        (u.org_name || '').toLowerCase().includes(q)
      );
    });
  }, [users, orgFilter, search]);

  const setUserEnabled = async (u: PortalUser, enable: boolean) => {
    setBusyId(u.id);
    setError(null);
    setNotice(null);
    try {
      const res = await api.post(`/api/crm/portal-admin/users/${u.id}/disable`, { enable });
      const revoked = res.data?.sessions_revoked ?? 0;
      setNotice(
        enable
          ? `${u.name} can sign in again.`
          : `${u.name} is disabled${revoked ? ` — ${revoked} live session${revoked === 1 ? '' : 's'} signed out immediately` : ''}.`
      );
      await load();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusyId(null);
    }
  };

  const setOrgStatus = async (o: PortalOrg, status: 'active' | 'disabled') => {
    setBusyId(o.id);
    setError(null);
    setNotice(null);
    try {
      await api.post(`/api/crm/portal-admin/orgs/${o.id}/status`, { status });
      setNotice(
        status === 'disabled'
          ? `${o.name} is disabled — everyone there is signed out of the portal now.`
          : `${o.name} is active again.`
      );
      await load();
    } catch (e) {
      setError(errMsg(e));
    } finally {
      setBusyId(null);
    }
  };

  const revokeInvite = async (u: PortalUser) => {
    setBusyId(u.id);
    setError(null);
    setNotice(null);
    try {
      await api.post(`/api/crm/portal-admin/users/${u.id}/revoke-invite`, {});
      setNotice(`The emailed sign-in link for ${u.email} is dead. Invite the right address whenever you're ready.`);
      setRevokeInviteUser(null);
      await load();
    } catch (e) {
      // The 409 body ("No live invite to revoke") surfaces verbatim via errMsg.
      setError(errMsg(e));
      setRevokeInviteUser(null);
    } finally {
      setBusyId(null);
    }
  };

  const reinvite = async (u: PortalUser) => {
    setBusyId(u.id);
    setError(null);
    setNotice(null);
    try {
      const res = await api.post('/api/crm/portal-admin/users', {
        org_id: u.org_id,
        name: u.name,
        email: u.email,
        credential: u.credential || undefined,
        role: u.role,
        mobile_phone: u.mobile_phone || undefined,
      });
      setNotice(res.data?.email_error || `A fresh invite is on its way to ${u.email}. The previous link no longer works.`);
      await load();
    } catch (e) {
      if (e && typeof e === 'object' && (e as any)?.response?.status === 503) setNotConfigured(true);
      setError(errMsg(e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="space-y-4">
      <PageHeader
        title="Clinician portal"
        icon={ShieldCheck}
        count={users.length}
        subtitle="Referring practices and the clinicians who can sign in to refer patients."
        actions={tab !== 'users' ? null : (
          <div className="flex items-center gap-2">
            <RefreshButton onClick={load} loading={loading} />
            <button type="button" className={secondaryBtnCls} onClick={() => setShowOrg(true)}>
              <Plus className="mr-1.5 h-4 w-4" /> Practice
            </button>
            <button
              type="button"
              className={primaryBtnCls}
              onClick={() => setShowInvite(true)}
              disabled={orgs.filter((o) => o.status === 'active').length === 0}
              title={
                orgs.filter((o) => o.status === 'active').length === 0
                  ? 'Add a practice first'
                  : 'Invite a clinician'
              }
            >
              <UserPlus className="mr-1.5 h-4 w-4" /> Invite clinician
            </button>
          </div>
        )}
      />

      {/* Underline tabs (design system). The count badge on Referrals is the one
          number a staff member needs at a glance: how many name mismatches are
          sitting there with nothing created for them. */}
      <div className="border-b border-gray-200 dark:border-gray-700">
        <nav className="-mb-px flex gap-4" aria-label="Clinician portal sections">
          {([
            { key: 'users', label: 'Practices & users', icon: Building2, badge: 0 },
            { key: 'referrals', label: 'Referrals', icon: Send, badge: collisions },
            { key: 'grants', label: 'Grants', icon: KeyRound, badge: 0 },
            { key: 'audit', label: 'Audit', icon: ScrollText, badge: 0 },
          ] as const).map(({ key, label, icon: Icon, badge }) => (
            <button
              key={key}
              type="button"
              onClick={() => setTab(key)}
              aria-current={tab === key ? 'page' : undefined}
              aria-label={badge > 0 ? `${label} (${badge} needing review)` : undefined}
              className={`inline-flex min-h-[44px] items-center gap-1.5 border-b-2 px-2.5 text-sm font-medium transition-colors duration-150 focus:outline-none focus:ring-2 focus:ring-primary-500 sm:min-h-0 sm:py-2 ${
                tab === key
                  ? 'border-primary-600 text-primary-700 dark:text-primary-400'
                  : 'border-transparent text-gray-500 hover:border-gray-300 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200'
              }`}
            >
              <Icon className="h-4 w-4" /> {label}
              {badge > 0 && (
                <span className="rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700 dark:bg-red-900/40 dark:text-red-300">
                  {badge}
                </span>
              )}
            </button>
          ))}
        </nav>
      </div>

      {tab === 'referrals' && (
        <PortalReferralsPanel initialStatus={initialReferralStatus} onCollisionCount={setCollisions} />
      )}
      {tab === 'grants' && (
        <PortalGrantsPanel
          clinicians={users
            .filter((u) => u.status === 'active' && u.org_status === 'active')
            .map((u) => ({ id: u.id, name: u.name, email: u.email, status: u.status }))}
        />
      )}
      {tab === 'audit' && <PortalAuditPanel />}

      {tab === 'users' && (
      <>
      <FilterBar>
        <SearchInput value={search} onChange={setSearch} placeholder="Search name, email or practice" />
        <FilterSelect
          label="Practice"
          auto
          value={orgFilter}
          onChange={setOrgFilter}
          options={[{ value: '', label: 'All practices' }, ...orgs.map((o) => ({ value: o.id, label: o.name }))]}
        />
      </FilterBar>

      {notConfigured && (
        <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
          The portal is not switched on yet — invites need the portal secrets configured on the API. Practices can be set
          up now; invites will send once it is live.
        </p>
      )}
      {error && (
        <Alert tone="error" icon={false}>{error}</Alert>
      )}
      {notice && (
        <Alert tone="success" icon={false}>{notice}</Alert>
      )}

      {/* Practices */}
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-2.5 dark:border-gray-700">
          <Building2 className="h-4 w-4 text-primary-600 dark:text-primary-400" />
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Practices <span className="font-normal text-gray-500">({orgs.length})</span>
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Practice</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Contact</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Users</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Status</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {orgs.length === 0 && !loading && (
                <tr>
                  <td colSpan={5} className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                    No referring practices yet. Add one, then invite its clinicians.
                  </td>
                </tr>
              )}
              {orgs.map((o) => (
                <tr key={o.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-4 py-2.5 text-sm font-medium text-gray-900 dark:text-gray-100">{o.name}</td>
                  <td className="px-4 py-2.5 text-sm text-gray-600 dark:text-gray-400">
                    {o.phone || '—'}
                    {o.fax ? ` · fax ${o.fax}` : ''}
                  </td>
                  <td className="px-4 py-2.5 text-sm text-gray-600 dark:text-gray-400">
                    {o.users.active} active
                    {o.users.invited ? `, ${o.users.invited} invited` : ''}
                    {o.users.disabled ? `, ${o.users.disabled} disabled` : ''}
                  </td>
                  <td className="px-4 py-2.5">
                    <Badge tone={STATUS_TONE[o.status]}>{o.status}</Badge>
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        className={iconBtnCls}
                        aria-label={`Edit ${o.name}`}
                        title="Edit practice details"
                        disabled={busyId === o.id}
                        onClick={() => setEditOrg(o)}
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        className={secondaryBtnCls}
                        disabled={busyId === o.id}
                        onClick={() => setOrgStatus(o, o.status === 'active' ? 'disabled' : 'active')}
                      >
                        {busyId === o.id && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                        {o.status === 'active' ? 'Disable' : 'Enable'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Clinicians */}
      <div className="overflow-hidden rounded-xl border border-gray-200 bg-white dark:border-gray-700 dark:bg-gray-800">
        <div className="flex items-center gap-2 border-b border-gray-200 px-4 py-2.5 dark:border-gray-700">
          <UserPlus className="h-4 w-4 text-primary-600 dark:text-primary-400" />
          <h2 className="text-sm font-semibold text-gray-900 dark:text-gray-100">
            Clinicians <span className="font-normal text-gray-500">({filtered.length})</span>
          </h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-800/50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Name</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Practice</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Role</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500 dark:text-gray-400">Last sign-in</th>
                <th className="px-4 py-3" />
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
              {loading && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                    <Loader2 className="mx-auto h-4 w-4 animate-spin" />
                  </td>
                </tr>
              )}
              {!loading && filtered.length === 0 && (
                <tr>
                  <td colSpan={6} className="px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400">
                    {users.length === 0 ? 'No clinicians invited yet.' : 'No clinicians match that filter.'}
                  </td>
                </tr>
              )}
              {filtered.map((u) => (
                <tr key={u.id} className="hover:bg-gray-50 dark:hover:bg-gray-700/50">
                  <td className="px-4 py-2.5 text-sm">
                    <div className="font-medium text-gray-900 dark:text-gray-100">
                      {u.name}
                      {u.credential ? <span className="ml-1 text-gray-500">{u.credential}</span> : null}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400">{u.email}</div>
                  </td>
                  <td className="px-4 py-2.5 text-sm text-gray-600 dark:text-gray-400">
                    {u.org_name || '—'}
                    {u.org_status === 'disabled' && (
                      <span className="ml-1 text-xs text-gray-500">(practice disabled)</span>
                    )}
                  </td>
                  <td className="px-4 py-2.5 text-sm text-gray-600 dark:text-gray-400">{u.role}</td>
                  <td className="px-4 py-2.5">
                    <Badge tone={STATUS_TONE[u.status]}>{u.status}</Badge>
                    {u.invite_expired && <span className="ml-1 text-xs text-amber-700 dark:text-amber-300">link expired</span>}
                  </td>
                  <td className="px-4 py-2.5 text-sm text-gray-600 dark:text-gray-400">
                    {u.last_login_at ? new Date(u.last_login_at).toLocaleDateString() : '—'}
                  </td>
                  <td className="px-4 py-2.5 text-right">
                    <div className="flex items-center justify-end gap-2">
                      <button
                        type="button"
                        className={secondaryBtnCls}
                        disabled={busyId === u.id}
                        onClick={() => setEditUser(u)}
                      >
                        <Pencil className="mr-1.5 h-4 w-4" />
                        Edit
                      </button>
                      <button
                        type="button"
                        className={secondaryBtnCls}
                        disabled={busyId === u.id}
                        title="Where this clinician is signed in — and the sign-out-everywhere switch"
                        onClick={() => setSessionsUser(u)}
                      >
                        <MonitorSmartphone className="mr-1.5 h-4 w-4" />
                        Sessions
                      </button>
                      {u.status !== 'disabled' && (
                        <button type="button" className={secondaryBtnCls} disabled={busyId === u.id} onClick={() => reinvite(u)}>
                          {u.status === 'invited' ? 'Re-send invite' : 'Send new invite'}
                        </button>
                      )}
                      {u.invite_pending && (
                        <button
                          type="button"
                          className={secondaryBtnCls}
                          disabled={busyId === u.id}
                          onClick={() => setRevokeInviteUser(u)}
                        >
                          Revoke invite
                        </button>
                      )}
                      <button
                        type="button"
                        className={secondaryBtnCls}
                        disabled={busyId === u.id}
                        onClick={() => setUserEnabled(u, u.status === 'disabled')}
                      >
                        {busyId === u.id && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                        {u.status === 'disabled' ? 'Enable' : 'Disable'}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      </>
      )}

      {showOrg && <CreateOrgModal onClose={() => setShowOrg(false)} onSaved={load} />}
      {showInvite && (
        <InviteUserModal
          orgs={orgs}
          onClose={() => setShowInvite(false)}
          onSaved={(note) => {
            setNotice(note || 'Invite sent.');
            load();
          }}
        />
      )}
      {editOrg && (
        <EditOrgModal
          org={editOrg}
          onClose={() => setEditOrg(null)}
          onSaved={() => {
            setNotice(`${editOrg.name} updated.`);
            load();
          }}
        />
      )}
      {editUser && (
        <EditUserModal
          user={editUser}
          orgs={orgs}
          onClose={() => setEditUser(null)}
          onSaved={(note) => {
            setNotice(note);
            load();
          }}
        />
      )}
      {sessionsUser && <SessionsModal user={sessionsUser} onClose={() => setSessionsUser(null)} />}
      {revokeInviteUser && (
        <Modal isOpen onClose={() => setRevokeInviteUser(null)} title="Revoke invite" maxWidth="max-w-md">
          <div className="space-y-3">
            <p className="text-sm text-gray-700 dark:text-gray-300">
              This kills the emailed sign-in link immediately. If this person was invited at a wrong address, revoke,
              then invite the right address.
            </p>
            <p className="text-sm text-gray-500 dark:text-gray-400">
              Revoke the live invite for <span className="font-medium text-gray-700 dark:text-gray-200">{revokeInviteUser.email}</span>?
            </p>
            <div className="flex justify-end gap-2 pt-1">
              <button type="button" className={secondaryBtnCls} onClick={() => setRevokeInviteUser(null)} disabled={busyId === revokeInviteUser.id}>
                Cancel
              </button>
              <button
                type="button"
                className={dangerBtnCls}
                onClick={() => revokeInvite(revokeInviteUser)}
                disabled={busyId === revokeInviteUser.id}
              >
                {busyId === revokeInviteUser.id && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
                Revoke invite
              </button>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}
